/**
 * Statistics Calculations
 *
 * 통계 데이터 계산을 위한 유틸리티 함수들
 */

import type { Transaction } from '../types/Transaction'
import type { Category } from '../../category/types/Category'
import type {
  Period,
  PeriodType,
  CategoryStat,
  TimeSeriesPoint
} from '../types/Statistics'

/**
 * 날짜 포맷팅 헬퍼 함수
 */
function formatDate(date: Date, format: string = 'YYYY-MM-DD'): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  if (format === 'YYYY-MM-DD') {
    return `${year}-${month}-${day}`
  } else if (format === 'YYYY-MM') {
    return `${year}-${month}`
  } else if (format === 'MM/DD') {
    return `${month}/${day}`
  }

  return `${year}-${month}-${day}`
}

/**
 * 1. 기간 생성 함수
 */
export function createPeriod(
  type: PeriodType,
  referenceDate: Date = new Date()
): Period {
  const start = new Date(referenceDate)
  const end = new Date(referenceDate)

  switch (type) {
    case 'week':
      // 최근 7일
      start.setDate(start.getDate() - 6)
      return {
        type: 'week',
        startDate: formatDate(start),
        endDate: formatDate(end),
        label: `최근 7일 (${formatDate(start, 'MM/DD')} ~ ${formatDate(end, 'MM/DD')})`
      }

    case 'month':
      // 해당 월 전체
      start.setDate(1)
      end.setMonth(end.getMonth() + 1, 0)
      return {
        type: 'month',
        startDate: formatDate(start),
        endDate: formatDate(end),
        label: `${start.getFullYear()}년 ${start.getMonth() + 1}월`
      }

    case 'year':
      // 해당 년도 전체
      start.setMonth(0, 1)
      end.setMonth(11, 31)
      return {
        type: 'year',
        startDate: formatDate(start),
        endDate: formatDate(end),
        label: `${start.getFullYear()}년`
      }
  }
}

/**
 * 2. 카테고리별 집계
 */
export function aggregateByCategory(
  transactions: Transaction[],
  categories: Category[]
): CategoryStat[] {
  // 지출만 필터링
  const expenses = transactions.filter(t => t.type === 'expense')

  // 카테고리별 그룹화
  const grouped = new Map<string, Transaction[]>()
  for (const tx of expenses) {
    const categoryId = tx.majorCategoryId
    if (!grouped.has(categoryId)) {
      grouped.set(categoryId, [])
    }
    grouped.get(categoryId)!.push(tx)
  }

  // 총 지출 계산
  const totalExpense = expenses.reduce((sum, tx) => sum + tx.amount, 0)

  // CategoryStat 배열 생성
  const stats: CategoryStat[] = []
  for (const [categoryId, txs] of grouped.entries()) {
    const category = categories.find(c => c.id === categoryId)
    if (!category) continue

    const amount = txs.reduce((sum, tx) => sum + tx.amount, 0)
    stats.push({
      categoryId,
      categoryName: category.name,
      categoryIcon: category.icon,
      amount,
      percentage: totalExpense > 0 ? (amount / totalExpense) * 100 : 0,
      transactionCount: txs.length,
      transactions: txs
    })
  }

  // 금액 내림차순 정렬
  return stats.sort((a, b) => b.amount - a.amount)
}

/**
 * 3. 시계열 데이터 생성
 */
export function createTimeSeriesData(
  transactions: Transaction[],
  period: Period
): TimeSeriesPoint[] {
  const { type, startDate, endDate } = period
  const start = new Date(startDate)
  const end = new Date(endDate)

  // 날짜별 맵 초기화
  const dataMap = new Map<string, TimeSeriesPoint>()

  if (type === 'week') {
    // 7일간 일별 데이터
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDate(d)
      dataMap.set(dateStr, {
        date: dateStr,
        label: formatDate(d, 'MM/DD'),
        income: 0,
        expense: 0,
        net: 0
      })
    }
  } else if (type === 'month') {
    // 월간 일별 데이터
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = formatDate(d)
      dataMap.set(dateStr, {
        date: dateStr,
        label: formatDate(d, 'MM/DD'),
        income: 0,
        expense: 0,
        net: 0
      })
    }
  } else {
    // 연간 월별 데이터
    for (let m = 0; m < 12; m++) {
      const monthStart = new Date(start.getFullYear(), m, 1)
      const monthKey = formatDate(monthStart, 'YYYY-MM')
      dataMap.set(monthKey, {
        date: monthKey,
        label: `${m + 1}월`,
        income: 0,
        expense: 0,
        net: 0
      })
    }
  }

  // 트랜잭션 데이터 집계
  for (const tx of transactions) {
    const key = type === 'year'
      ? tx.date.substring(0, 7)  // YYYY-MM
      : tx.date                   // YYYY-MM-DD

    const point = dataMap.get(key)
    if (!point) continue

    if (tx.type === 'income') {
      point.income += tx.amount
    } else {
      point.expense += tx.amount
    }
    // 기존: point.net = point.income - point.expense (각 기간의 순증액)
    // 누적 잔액은 아래에서 별도로 계산
  }

  // 누적 잔액 계산 (기간별 순증액을 누적)
  const pointsArray = Array.from(dataMap.values())
  let cumulativeNet = 0

  for (const point of pointsArray) {
    const periodNet = point.income - point.expense  // 해당 기간의 순증액
    cumulativeNet += periodNet  // 누적 합산
    point.net = cumulativeNet   // 누적 잔액을 net에 저장
  }

  return pointsArray
}

/**
 * 4. 전기 대비 계산
 */
export function calculateComparison(
  currentTransactions: Transaction[],
  previousTransactions: Transaction[]
): { incomeChange: number; expenseChange: number } {
  const currentIncome = currentTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0)

  const currentExpense = currentTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0)

  const previousIncome = previousTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + t.amount, 0)

  const previousExpense = previousTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0)

  return {
    incomeChange: previousIncome > 0
      ? ((currentIncome - previousIncome) / previousIncome) * 100
      : 0,
    expenseChange: previousExpense > 0
      ? ((currentExpense - previousExpense) / previousExpense) * 100
      : 0
  }
}
